import React, { useEffect, useMemo, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import './Scripts.css'
import ComponentBox from '../../components/ComponentBox/ComponentBox'
import { useNavigate } from 'react-router-dom'
import { renderDateTime } from '../../utils/dateFormatter'
import {
  getAllScript,
  removeScript,
  getScript,
  setEntityError,
} from '../../redux/slices'
import { DefaultState } from '../../types'
import { Container, Col, Row, ModalBody } from 'reactstrap'
import HeaderBar from '../../components/HeaderBar/HeaderBar'
import ICSActionButton from '../../components/ICS/ActionButton/ICSActionButton'
import { ICSDataCellDetailView } from '../../components/ICSDataCellDetailView/ICSDataCellDetailView'
import ICSErrorCard from '../../components/ICSErrorCard/ICSErrorCard'
import { ICSModal } from '../../components/ICSModal/ICSModal'
import { TanStackDatagridWithExport } from '../../components'
import {
  TanStackDatagridColumn,
  TanStackDatagridFilter,
  TanStackDatagridPagination,
  TanStackDatagridRowAction,
  TanStackDatagridSort
} from '../../components/TanStackDataGrid/TanStackDatagrid'
import ScriptUpload from './ScriptUpload/ScriptUpload'
import { AppDispatch } from '../../services/lib'
import { IPAScript } from '../../types'

const Scripts = () => {
  const navigate = useNavigate()
  const scriptsState = useSelector((state: DefaultState) => state.scripts)
  const scriptsError = useSelector((state: DefaultState) => state.scripts.error)
  const isAdmin: boolean | undefined = useSelector(
    (state: DefaultState) => state.session.data.user?.roles?.includes('admin')
  )
  const scriptsArray = useMemo(
    () =>
      scriptsState.ids
        .map((id) => scriptsState.entities[id])
        .filter(Boolean) as IPAScript[],
    [scriptsState.entities, scriptsState.ids]
  )
  const [showUploadModal, setShowUploadModal] = useState<boolean>(false)
  const [sort, setSort] = useState<TanStackDatagridSort | null>(null)
  const [filters, setFilters] = useState<TanStackDatagridFilter[]>([])
  const [pagination, setPagination] = useState<TanStackDatagridPagination>({
    pageIndex: 0,
    pageSize: 10
  })
  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>({})
  const dispatch: AppDispatch = useDispatch()

  useEffect(() => {
    // prevents console error for invalid token
    if (window.location.pathname != '/ui2/login') {
      const initialData = async () => {
        await dispatch(getAllScript())
      }

      initialData()
    }
  }, [dispatch])

  function scriptAddButtonClicked() {
    if (!isAdmin) return
    setShowUploadModal(true)
  }

  const hideModal = () => {
    dispatch(
      setEntityError({
        sliceName: 'scripts',
        entityName: 'Script',
        error: null
      })
    )
    setShowUploadModal(false)
  }

  const columns = useMemo<TanStackDatagridColumn<IPAScript>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        accessor: (script) => script.name || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'description',
        header: 'Description',
        accessor: (script) => script.description || '',
        cell: (script) => (
          <ICSDataCellDetailView
            value={script.description || ''}
            data={script}
            row={{ data: script }}
            modalWidth='35vw'
            modalHeight='20vh'
          />
        ),
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'userName',
        header: 'User',
        accessor: (script) => script.user?.name || '',
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      },
      {
        id: 'created_at',
        header: 'Create Date',
        accessor: (script) => script.created_at || '',
        cell: (script) =>
          renderDateTime({
            value: script.created_at || ''
          }),
        sortable: true,
        filterable: true,
        resizable: true,
        hideable: true
      }
    ],
    []
  )

  const actions = useMemo<TanStackDatagridRowAction<IPAScript>[]>(
    () => [
      {
        id: 'configurations',
        icon: 'lucide#cog',
        label: 'Configurations',
        onClick: (script) => {
          dispatch(getScript(script.id))
          navigate(`/ui2/scripts/${script.id}/configurations`)
        }
      },
      {
        id: 'delete',
        icon: 'lucide#trash',
        label: 'Delete',
        hidden: () => !isAdmin,
        onClick: async (script) => {
          const shouldDelete = window.confirm(
            `Are you sure you want to delete the following Script: ${script.name}?`
          )

          if (shouldDelete) {
            await dispatch(removeScript(script.id))
            await dispatch(getAllScript())
          }
        }
      }
    ],
    [dispatch, isAdmin, navigate]
  )

  return (
    <>
      <Container fluid={true}>
        <ICSModal
          visible={showUploadModal}
          onHiding={hideModal}
          title='Upload Script'
        >
          <ModalBody style={{ padding: '0px' }}>
            <span>
              {showUploadModal && <ScriptUpload hideModal={hideModal} />}
            </span>
          </ModalBody>
        </ICSModal>
        <Row>
          <Col className='wivi-col'>
            <HeaderBar
              origin='IPA'
              routes={[
                {
                  title: 'Scripts',
                },
              ]}
              help={{
                title: 'Analysis Scripts',
                text: 'Analysis Scripts are the data processor assigned to an Analyzer. Users can see all Analyzer Scripts available to their organization. All users can upload Analyzer Scripts but it cannot be used by anyone until reviewed and approved by an administrator. AnalyzerScripts can be deleted by their owner and administrators. Scripts may be written in Python. R support may be added in the future.',
              }}
            />
          </Col>
        </Row>
        <Row>
          <Col xs={{ size: 12 }} className='wivi-col'>
            <ComponentBox>
              {scriptsError?.code === 'error' && !showUploadModal ? (
                <ICSErrorCard
                  code={scriptsError.code}
                  message={scriptsError.message}
                  fields={scriptsError.fields}
                />
              ) : (
                <div className='d-flex flex-column h-100'>
                  <div className='flex-grow-1 min-vh-0'>
                    <TanStackDatagridWithExport
                      toolbarContent={
                        isAdmin ? (
                          <ICSActionButton
                            id='scriptUploadButton'
                            type='success'
                            width={65}
                            height={35}
                            stylingMode='contained'
                            onClick={scriptAddButtonClicked}
                            text='Add'
                            disabled={!isAdmin}
                          />
                        ) : null
                      }
                      data={scriptsArray}
                      columns={columns}
                      actions={actions}
                      getRowId={(script) => String(script.id)}
                      sort={sort}
                      onSortChange={setSort}
                      filters={filters}
                      onFilterChange={setFilters}
                      enablePagination
                      pagination={pagination}
                      onPaginationChange={setPagination}
                      columnVisibility={columnVisibility}
                      onColumnVisibilityChange={setColumnVisibility}
                      enableColumnSelection
                    />
                  </div>
                </div>
              )}
            </ComponentBox>
          </Col>
        </Row>
      </Container>
    </>
  )
}
export default Scripts
